import { ComboboxItem, OptionsFilter } from '@mantine/core';
import { Link } from '@zorro/design-system/v2/Link';
import { Select } from '@zorro/design-system/v2/Select';
import { naicsCodesDatabase } from '@zorro/shared/databases';
import { Stack, Text, brand } from '@zorro/web/design-system';
import { forwardRef, memo } from 'react';

interface NaicsOption extends ComboboxItem {
  value: string;
  label: string;
}

const naicsLink = {
  name: 'read more',
  href: 'https://www.census.gov/naics/?48967',
};

const naicsCodesData = naicsCodesDatabase
  ? naicsCodesDatabase.map((naicsEntry) => ({
      value: naicsEntry.code,
      label: `${naicsEntry.code} - ${naicsEntry.title}`,
    }))
  : [];

const renderNaicsOption = ({ option }: { option: NaicsOption }) => {
  const title = option.label.split(' - ')[1];
  return (
    <Stack gap="0.15rem">
      <Text size="md" fw={500}>
        {title}
      </Text>
      <Text size="sm" c="gray.6">
        {option.value}
      </Text>
    </Stack>
  );
};

const naicsFilter: OptionsFilter = ({ options, search }) => {
  return (options as NaicsOption[]).filter(
    (option) =>
      `${option.label} ${option.value}`
        .toLowerCase()
        .includes(search.toLowerCase()) ||
      `${option.value} ${option.label} `
        .toLowerCase()
        .includes(search.toLowerCase())
  );
};

type Props = {
  isReadonlyMode?: boolean;
  isFinalizationMode?: boolean;
};

const _NaicsCodeInput = forwardRef<HTMLInputElement, Props>(
  ({ isReadonlyMode = false, isFinalizationMode = false, ...field }, ref) => {
    return (
      <>
        <Select
          {...field}
          ref={ref}
          isReadOnly={isReadonlyMode}
          required={isFinalizationMode}
          clearable
          searchable
          placeholder="Select code"
          label="NAICS code"
          data={naicsCodesData}
          size="md"
          renderOption={renderNaicsOption}
          filter={naicsFilter}
        />
        <Text size="sm" c={brand.zorroGray400} mt="xs">
          {'If you don’t know your NAICS code '}
          <Link href={naicsLink.href} target="_blank" rel="noopener noreferrer">
            {naicsLink.name}
          </Link>
        </Text>
      </>
    );
  }
);

export const NaicsCodeInput = memo(_NaicsCodeInput);
